import { useState, useRef, useEffect } from "react";

interface DropdownOption {
  value: string;
  label: string;
  address?: string;
}

interface CustomDropdownProps {
  options: DropdownOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export const CustomDropdown: React.FC<CustomDropdownProps> = ({
  options,
  value,
  onChange,
  placeholder = "Select an option",
  className = "",
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const selectedOption = options.find((opt) => opt.value === value);

  // Filter options based on search term
  const filteredOptions = options.filter((option) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      option.label.toLowerCase().includes(searchLower) ||
      (option.address && option.address.toLowerCase().includes(searchLower))
    );
  });

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full border p-3 text-left bg-white dark:bg-primary-800 flex justify-between items-center ${
          disabled
            ? "cursor-not-allowed opacity-50 bg-gray-50 dark:bg-primary-900 border-gray-200 dark:border-primary-700"
            : "text-primary-500 dark:text-white border-gray-200 dark:border-primary-700 hover:border-primary-400 dark:hover:border-primary-500 focus:outline-none focus:border-primary-600"
        }`}
      >
        <span className={!selectedOption ? "text-gray-400 dark:text-gray-500" : ""}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white dark:bg-primary-800 border border-gray-200 dark:border-primary-700 rounded-md shadow-lg">
          {/* Search input */}
          <div className="p-2 border-b border-gray-200 dark:border-primary-700 sticky top-0 bg-white dark:bg-primary-800">
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-primary-600 rounded-md focus:outline-none focus:border-primary-500 text-sm bg-white dark:bg-primary-900 dark:text-white"
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {/* Options list */}
          <div className="max-h-60 overflow-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <div
                  key={option.value}
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                    setSearchTerm("");
                  }}
                  className={`px-3 py-3 cursor-pointer hover:bg-primary-50 dark:hover:bg-primary-700 border-b border-gray-100 dark:border-primary-700 last:border-b-0 ${
                    value === option.value ? "bg-primary-50 dark:bg-primary-700" : ""
                  }`}
                >
                  <div className="font-medium text-primary-600 dark:text-white">{option.label}</div>
                  {option.address && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{option.address}</div>
                  )}
                </div>
              ))
            ) : (
              <div className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400 text-center">No results found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
